<template lang="pug">
.resident-careimage-row
  .name-column.resident-name(:id="'resident-' + id")
    .room-name {{ room }}
    .name {{ name }}
  .file-selecter
    MultiFileSelecter(
      ref="fileSelecter"
      :mimetypes="['image/jpeg', 'application/pdf']"
      width=300
      height=60
      limit=4
      :showPreview="true"
      :enablePreviewStatus="_existImage"
      @input="inputFiles"
      @preview="onPreview"
    )
  // ファイルが1つでも存在する場合はドラッグ可能なファイルリストを表示する
  draggable.draggable(ref="draggable" v-if="0 < value.length" v-model="_value" animation="200" ghostClass="ghost"
    @start="onDragStart(true)" @end="onDragEnd(false)" @change="onChange"
  )
    .image-column(v-for="(file, index) in value" :key="value.key")
      .file.d-flex.justify-content-start.align-items-center(
        :id="'file-' + id + '-' + index"
        :class="{ grabbing : isGrabbing }"
      )
        .file-name
          span {{ file.name }}
        .button-area
          button.trash-btn.btn(@click="onDelete(index)")
            .trash-icon
      b-popover(ref="popovers" :target="'file-' + id + '-' + index" placement="top" triggers="hover" :disabled="isGrabbing") {{ file.name }}
  // 最大ファイル数に満たない分は空のdiv要素を表示する
  // あわせて、バリデーション用のinput要素もここで作る
  .image-column(v-for="index in (limit - value.length)")
    input(:name="'file-' + id + '-' + (index - 1)" style="display: none;")

  CareimageModal(ref="careimageModal" id="careimage-modal" show-delete="false")

  PageLoading(:isBusy="isBusy" :message="loadingMessage")
</template>

<script lang="ts">
import { Component, Prop, Inject, Vue } from "vue-property-decorator";
import { Validator } from "vee-validate";
import draggable from "vuedraggable";

import CareimageCommonService, { CareimageFileModel } from "@/commons/services/CareimageCommon";

import PageLoading from "@/pc/basics/PageLoading.vue";
import MultiFileSelecter from "@/pc/basics/MultiFileSelecter.vue";
import CareimageModal from "@/pc/components/modals/CareimageModal.vue";

/**
 * 介護記録一括登録画面コンポーネント
 */
@Component({
  components: {
    PageLoading,
    CareimageModal,
    MultiFileSelecter,
    draggable
  }
})
export default class ResidentCareimageRow extends Vue {
  // 親コンポーネントのバリデーターをInjectionする
  @Inject("$validator") $validator!: Validator;

  /** 利用者とひもづく介護記録 */
  @Prop({ required: true })
  value!: Array<CareimageFileModel>;

  /** 日付 */
  @Prop({ required: true, type: Number })
  date!: number;

  /** 利用者ID */
  @Prop({ required: true, type: Number })
  id!: number;

  /** 利用者名 */
  @Prop({ required: true, type: String })
  name!: string;

  /** 部屋名 */
  @Prop({ required: true, type: String })
  room!: string;

  /** 登録ファイル数上限 */
  @Prop({ required: true, type: Number })
  limit!: number;

  /** ページ全体ローディング表示可否 */
  isBusy: boolean = false;
  /** ローディングメッセージ */
  loadingMessage: string = "";

  /** ドラッグされているかの状態 */
  isGrabbing: boolean = false;

  /** v-model getter */
  get _value(): Array<CareimageFileModel> {
    return this.value;
  }

  /** v-model setter */
  set _value(newValue: Array<CareimageFileModel>) {
    this.$emit("input", newValue);
    return;
  }

  /**
   * ### 介護記録の追加イベント処理
   *
   * - ファイルが上限数以上なら無視する
   * - バリデーションを行う
   * - バリデーションエラーでなければ、ファイルを親コンポーネントに渡す
   */
  public async inputFiles(files: Array<File>): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "読込み中...";

    const careimageCommonService: CareimageCommonService = new CareimageCommonService(this.$validator, "careimage-multiple-register-errors");
    const addFiles: Array<CareimageFileModel> = await careimageCommonService.createAddFile(files, this.name, this.limit, this.value.length);

    this.isBusy = false;

    // 追加ファイルがあれば親コンポーネントに渡す
    if (0 < addFiles.length) {
      this.$emit("add", addFiles);
    }

    return;
  }

  /**
   * プレビュー表示
   * S3画像URLを公開設定にしたURLを作る場合があるので、ローディングを出す
   *
   * @param resident 利用者
   */
  public async onPreview(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "読込み中...";

    const careimageModal: CareimageModal | Vue | Element | Vue[] | Element[] = this.$refs.careimageModal;
    if (careimageModal instanceof CareimageModal) {
      const date: string = this.$dateUtil.convertNumberToString(this.date, "yyyy年mm月dd日");
      const title: string = `${date}の${this.name}様`;
      const urls: Array<string> = [];
      for (const file of this.value) {
        urls.push(file.dataUrl);
      }
      careimageModal.show(title, false, urls);
    }

    this.isBusy = false;

    return;
  }

  /**
   * ### 親コンポーネントにファイル削除を通知する
   */
  public onDelete(index: number): void {
    this.$emit("delete", index);
    return;
  }

  /**
   * ### ファイルがドラッグ開始されたイベント処理
   *
   * - vuedraggableの挙動により、ドラッグでファイルを入れ替えるとhover対象の要素がズレる
   *   そのため、ドラッグ中はhoverのスタイルを無効にする
   *   - スタイルのon/offを切り替えるためにドラッグ状態を切り替える
   * - vuebootstrapのpopoverもズレるので、ドラッグ中は非表示にする
   */
  public onDragStart(grabbing: boolean): void {
    this.isGrabbing = grabbing;

    if (grabbing) {
      const popovers: any = this.$refs.popovers;
      for (let i = 0; i < popovers.length; i++) {
        popovers[i].$emit("close");
      }
    }

    return;
  }

  /**
   * ### ファイルがドラッグ終了したイベント処理
   *
   * - スタイルのon/offを切り替えるためにドラッグ状態を切り替える
   */
  public onDragEnd(grabbing: boolean): void {
    this.isGrabbing = grabbing;
    return;
  }

  /**
   * ### ファイルが並び替えられたら呼ばれるイベント処理
   *
   * - 並び替えが行われなければ呼ばれない
   * - 親コンポーネントに並び替えされたことを通知する
   */
  public onChange(changeEvent: any): void {
    this.$emit("change");
    return;
  }

  /**
   * 利用者にひもづく介護記録画像が存在するかを返す
   *
   * @param resident 利用者
   */
  get _existImage(): boolean {
    return 0 < this.value.length ? true : false;
  }
}
</script>
<style lang="scss" scoped>
@import "../../styles/pc/_colors.scss";
@import "../../styles/pc/_mixin.scss";

.resident-careimage-row {
  display: flex;
  flex-direction: row; // 配下containerを横に並べる
  align-items: stretch;
  flex-wrap: nowrap;
  // 画面幅が小さい場合に、stickyによる回り込みで子要素が画面外に行くとstickyが動作しなくなる
  // そのため子要素の幅分、親要素の幅をとるように指定
  width: max-content;
  & > div {
    border-left: solid 2px $adminlte-box-bordercolor;
    border-bottom: solid 2px $adminlte-box-bordercolor;
    // 回り込み時に下にある要素が見えてしまうので、背景色を指定
    background-color: $bg-default;
  }
  & > div:first-child {
    border-left: 0;
  }
  // 列内のコンテンツ固有スタイル
  .resident-name {
    width: 180px;
    padding: 8px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;

    .room-name {
      font-size: 14px;
      max-width: 100%;
    }
    .name {
      font-size: 18px;
      max-width: 100%;
    }
  }
  .careimage {
    padding: 8px;
  }
  .file {
    &:hover {
      cursor: grab;
      background-color: $btn-draggable-hover !important;
      transition: background-color 0.25s ease-out, box-shadow 0.25s ease-out, color 0.25s ease-out;
    }

    min-height: 72px;
    max-height: 72px;

    padding: 8px;
    @include font(16px);
    margin: 0;
    border-radius: 3px;
    transition: all 0.2s;
    border: 1px solid $form-border-color;
    padding: 8px;

    .file-name {
      max-height: 74px;
      min-width: 230px;
      max-width: 230px;
      margin: 0 8px 0 0;
      word-break: break-all;
      display: -webkit-box;
      // ブラウザがchrome限定なので、指定行数制限をかけれる
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 3;
      overflow: hidden;
    }
    .button-area {
      max-height: 74px;
      min-width: 32px;
      max-width: 32px;
      .trash-btn {
        font-size: 16px !important;
        height: 32px !important;
        padding: 2px 5px !important;
      }
      .trash-icon {
        @include svg-icon("../../assets/trash.svg", $font-base-color, 24px, 24px);
      }
    }
  }
  // ドラッグ範囲内のdiv要素を横に並べるようにスタイルを指定する
  .draggable {
    display: flex;
    flex-direction: row; // 配下containerを横に並べる
    align-items: stretch;
    flex-wrap: nowrap;
    // ドラッグエリアは.resident-careimage内のdiv要素だが
    // ドラッグエリア内のdiv要素に枠線をつけるので枠線無しにする
    border: 0 !important;

    // ドラッグエリア内の枠線
    & > div {
      border-left: solid 2px $adminlte-box-bordercolor;
      border-bottom: solid 2px $adminlte-box-bordercolor;
    }
  }
  .grabbing {
    &:hover {
      background-color: #fff !important;
    }
  }
  .ghost {
    .file {
      cursor: grabbing;
      background-color: $btn-draggable-hover !important;
    }
  }
  // 共通で使用する列のスタイル
  // 最低幅を指定しないと画面が小さい時に動的に幅が小さくなってしまう
  .name-column {
    min-width: 181px;
    // 利用者名列は固定したいのでstickyにする
    position: sticky;
    left: 0;
    z-index: 100;
  }
  .file-selecter {
    min-width: 320px;
    padding: 8px;
  }
  .image-column {
    min-width: 307px;
    max-width: 307px;
    padding: 8px;
  }
}
</style>
